<template>
  <page-header-wrapper>
    <div class="erp-list-page baidao-list-page">
      <div class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="8" :sm="24">
              <a-form-item label="关键字">
                <a-input v-model="query.keyword" allow-clear placeholder="排车单号 / 车辆 / 司机" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="状态">
                <a-select v-model="query.status" allow-clear placeholder="全部">
                  <a-select-option v-for="item in statusOptions" :key="item.value" :value="item.value">{{ item.label }}</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="10" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="reset">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <div class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>线路派车单</h3>
            <p>主表锁定车辆和司机，子表锁定配送任务和点位顺序。确认派车后才能确认发车，发车后进入配送签收。</p>
          </div>
          <div class="erp-toolbar-actions">
            <a-button icon="download" :disabled="!selectedRowKeys.length" :loading="exporting" @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          :row-key="rowKey"
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          bordered
          size="middle"
          :scroll="{ x: 1500 }"
          :row-selection="rowSelection"
          @change="onTableChange"
        >
          <template slot="routeNo" slot-scope="text, record"><a @click="openDetail(record)">{{ text }}</a></template>
          <template slot="qty" slot-scope="text">{{ qtyText(text) }}</template>
          <template slot="operation" slot-scope="text, record">
            <a @click="openDetail(record)">查看详情</a>
            <a-divider type="vertical" />
            <a v-if="exportingId !== String(record.id)" @click.prevent="exportRow(record)">导出</a>
            <span v-else style="color:rgba(0,0,0,.45)">导出中…</span>
            <a-divider type="vertical" />
            <a v-if="printingId !== String(record.id)" @click.prevent="printRow(record)">打印</a>
            <span v-else style="color:rgba(0,0,0,.45)">打印中…</span>
            <template v-if="record.status === 'pending_confirm'">
              <a-divider type="vertical" />
              <a @click="confirm(record)">确认派车</a>
            </template>
            <template v-if="record.status === 'dispatched'">
              <a-divider type="vertical" />
              <a @click="depart(record)">确认发车</a>
            </template>
          </template>
        </a-table>
      </div>
    </div>
    <a-drawer title="线路派车单详情" :visible="visible" width="980" @close="visible = false">
      <a-descriptions v-if="detail" bordered size="small" :column="3">
        <a-descriptions-item label="排车单号">{{ detail.routeNo }}</a-descriptions-item>
        <a-descriptions-item label="状态">{{ detail.statusName }}</a-descriptions-item>
        <a-descriptions-item label="配送日期">{{ detail.routeDate }}</a-descriptions-item>
        <a-descriptions-item label="发货仓库">{{ detail.warehouseName }}</a-descriptions-item>
        <a-descriptions-item label="规则方案">{{ detail.ruleName }}</a-descriptions-item>
        <a-descriptions-item label="车辆">{{ detail.vehicleNo }}</a-descriptions-item>
        <a-descriptions-item label="司机">{{ detail.driverName }}</a-descriptions-item>
        <a-descriptions-item label="计划发车">{{ detail.planDepart }}</a-descriptions-item>
        <a-descriptions-item label="装载率">{{ detail.loadRate }}</a-descriptions-item>
        <a-descriptions-item label="配送任务数">{{ detail.taskCount }}</a-descriptions-item>
        <a-descriptions-item label="客户点位">{{ detail.stopCount }}</a-descriptions-item>
        <a-descriptions-item label="总件数">{{ qtyText(detail.totalQty) }}</a-descriptions-item>
      </a-descriptions>
      <h4 style="margin:16px 0 8px">线路点位 / 配送任务明细</h4>
      <a-table row-key="taskId" size="small" bordered :pagination="false" :data-source="detail ? detail.stops : []" :columns="stopColumns" :scroll="{ x: 1100 }">
        <template slot="qty" slot-scope="text">{{ qtyText(text) }}</template>
      </a-table>
    </a-drawer>
  </page-header-wrapper>
</template>

<script>
import { confirmDeliveryRoute, departDeliveryRoute, exportDeliveryRoute, getDeliveryRoute, pageDeliveryRoute } from '@/api/erp/deliveryDispatch'
import { downloadPdfBlob, exportPdfFilename, printPdfBlob } from '@/utils/erpDeliveryPdf'

function qtyText (value) {
  if (value === null || value === undefined || value === '') return '—'
  const number = Number(value)
  return Number.isNaN(number) ? value : String(number)
}

export default {
  name: 'ErpDeliveryRoute',
  data () {
    return {
      loading: false,
      exporting: false,
      exportingId: null,
      printingId: null,
      rows: [],
      selectedRowKeys: [],
      visible: false,
      detail: null,
      query: { keyword: '', status: undefined },
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` },
      statusOptions: [
        { value: 'pending_confirm', label: '待确认' },
        { value: 'dispatched', label: '已派车' },
        { value: 'delivering', label: '配送中' },
        { value: 'signed', label: '已签收' }
      ],
      columns: [
        { title: '排车单号', dataIndex: 'routeNo', width: 180, scopedSlots: { customRender: 'routeNo' } },
        { title: '配送日期', dataIndex: 'routeDate', width: 120 },
        { title: '发货仓库', dataIndex: 'warehouseName', width: 140 },
        { title: '规则方案', dataIndex: 'ruleName', width: 120 },
        { title: '车辆', dataIndex: 'vehicleNo', width: 120 },
        { title: '司机', dataIndex: 'driverName', width: 100 },
        { title: '任务数', dataIndex: 'taskCount', width: 80 },
        { title: '点位', dataIndex: 'stopCount', width: 80 },
        { title: '总件数', dataIndex: 'totalQty', width: 90, scopedSlots: { customRender: 'qty' } },
        { title: '装载率', dataIndex: 'loadRate', width: 90 },
        { title: '计划发车', dataIndex: 'planDepart', width: 120 },
        { title: '状态', dataIndex: 'statusName', width: 100 },
        { title: '操作', dataIndex: 'operation', width: 300, fixed: 'right', scopedSlots: { customRender: 'operation' } }
      ],
      stopColumns: [
        { title: '顺序', dataIndex: 'sortNo', width: 70 },
        { title: '配送任务', dataIndex: 'taskNo', width: 170 },
        { title: '来源发货单', dataIndex: 'shipNo', width: 160 },
        { title: '收货方', dataIndex: 'receiverName', width: 150 },
        { title: '收货地址', dataIndex: 'receiverAddress', ellipsis: true },
        { title: '时间窗', dataIndex: 'requiredArrival', width: 120 },
        { title: '件数', dataIndex: 'pieceQty', width: 80, scopedSlots: { customRender: 'qty' } },
        { title: '预计到达', dataIndex: 'eta', width: 100, customRender: text => text || '—' },
        { title: '风险', dataIndex: 'risk', width: 100 }
      ]
    }
  },
  created () { this.load() },
  computed: {
    rowSelection () {
      const self = this
      return {
        selectedRowKeys: self.selectedRowKeys,
        onChange (keys) {
          self.selectedRowKeys = (keys || []).map(key => String(key))
        }
      }
    }
  },
  methods: {
    qtyText,
    rowKey (record) { return String(record.id) },
    load () {
      this.loading = true
      pageDeliveryRoute({
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize,
        keyword: this.query.keyword,
        status: this.query.status
      }).then(res => {
        const page = res.data || {}
        this.rows = page.records || []
        this.pagination.total = Number(page.total || 0)
      }).finally(() => { this.loading = false })
    },
    search () { this.pagination.current = 1; this.load() },
    reset () {
      this.query = { keyword: '', status: undefined }
      this.selectedRowKeys = []
      this.search()
    },
    pdfBlobFromResponse (res) {
      return (res && res.data !== undefined ? res.data : res)
    },
    async doExport () {
      if (!this.selectedRowKeys.length) {
        this.$message.warning('请先勾选要导出的线路派车单')
        return
      }
      this.exporting = true
      try {
        const ids = this.selectedRowKeys.map(id => (id != null ? String(id) : id))
        const res = await exportDeliveryRoute(ids)
        await downloadPdfBlob(this.pdfBlobFromResponse(res), exportPdfFilename('线路派送确认单'))
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    async exportRow (record) {
      const id = record && record.id != null ? String(record.id) : ''
      if (!id) return
      this.exportingId = id
      try {
        const res = await exportDeliveryRoute([id])
        const prefix = record.routeNo ? `线路派送确认单_${record.routeNo}` : '线路派送确认单'
        await downloadPdfBlob(this.pdfBlobFromResponse(res), exportPdfFilename(prefix))
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exportingId = null
      }
    },
    async printRow (record) {
      const id = record && record.id != null ? String(record.id) : ''
      if (!id) return
      this.printingId = id
      try {
        const res = await exportDeliveryRoute([id])
        await printPdfBlob(this.pdfBlobFromResponse(res))
      } catch (e) {
        this.$message.error((e && e.message) || '打印失败')
      } finally {
        this.printingId = null
      }
    },
    onTableChange (pager) {
      this.pagination.current = pager.current
      this.pagination.pageSize = pager.pageSize
      this.load()
    },
    openDetail (record) {
      getDeliveryRoute(record.id).then(res => {
        this.detail = res.data
        this.visible = true
      })
    },
    confirm (record) {
      this.$confirm({
        title: '确认派车',
        content: '确认后锁定车辆、司机和配送顺序。',
        onOk: () => confirmDeliveryRoute(record.id).then(() => {
          this.$message.success('派车已确认')
          this.load()
        })
      })
    },
    depart (record) {
      this.$confirm({
        title: '确认发车',
        content: '确认后任务进入配送中，并生成配送签收。',
        onOk: () => departDeliveryRoute(record.id).then(() => {
          this.$message.success('已确认发车，请到配送签收处理')
          this.load()
        })
      })
    }
  }
}
</script>
